'use client';

import { useEffect, useState } from 'react';
import { Download, BookOpen, Calendar, Loader2 } from 'lucide-react';

interface Book {
  id: string;
  previewUrls: string[];
  pdfUrl: string | null;
  pages: number;
  watermark: boolean;
  createdAt: string;
  prompt: string;
}

interface BooksGalleryProps {
  session: any;
  title?: string;
}

export function BooksGallery({ session, title = 'Your Magical Library' }: BooksGalleryProps) {
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (session?.user) {
      fetchBooks();
    }
  }, [session]);

  const fetchBooks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/my-books');
      const data = await response.json();
      
      if (response.ok) {
        setBooks(data.books || []);
      } else {
        setError(data.error || 'Failed to load books');
      }
    } catch (err) {
      console.error('Error fetching books:', err);
      setError('Failed to load books');
    } finally {
      setLoading(false);
    }
  };

  if (!session) {
    return null;
  }

  if (loading) {
    return (
      <div style={{
        padding: '3rem 1.5rem',
        textAlign: 'center',
        maxWidth: '1100px',
        margin: '0 auto'
      }}>
        <Loader2 className="loading-spinner" size={48} style={{ margin: '0 auto' }} />
        <p style={{ marginTop: '1rem', color: 'var(--color-text-secondary)' }}>
          Loading your magical library...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '3rem 1.5rem',
        textAlign: 'center',
        maxWidth: '1100px',
        margin: '0 auto'
      }}>
        <p style={{ color: 'var(--color-error)' }}>{error}</p>
      </div>
    );
  }

  if (books.length === 0) {
    return null;
  }

  return (
    <>
      <style jsx>{`
        .gallery-container {
          padding: 3rem 1.5rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .gallery-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .gallery-title {
          font-size: 2.5rem;
          font-weight: 900;
          margin-bottom: 0.75rem;
          line-height: 1.2;
        }

        .gallery-title .gradient-text {
          background: linear-gradient(135deg, #0EA5E9 0%, #8B5CF6 50%, #F59E0B 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .gallery-subtitle {
          font-size: 1.125rem;
          color: var(--color-text-secondary);
          font-weight: 500;
        }

        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 2rem;
        }

        .book-card {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.98));
          border-radius: 24px;
          overflow: hidden;
          border: 2px solid rgba(168, 216, 230, 0.3);
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .book-card:hover {
          transform: translateY(-8px);
          border-color: rgba(168, 216, 230, 0.6);
          box-shadow: 0 12px 40px rgba(168, 216, 230, 0.3),
                      0 8px 20px rgba(201, 182, 228, 0.2);
        }

        .book-preview {
          position: relative;
          width: 100%;
          padding-top: 75%; /* 4:3 aspect ratio */
          background: linear-gradient(135deg, #A8D8E6 0%, #C9B6E4 50%, #F8C8A0 100%);
          overflow: hidden;
        }

        .book-preview-img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .book-card:hover .book-preview-img {
          transform: scale(1.05);
        }

        .book-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          padding: 0.5rem 1rem;
          border-radius: 12px;
          font-size: 0.875rem;
          font-weight: 700;
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .badge-demo {
          background: rgba(168, 216, 230, 0.9);
          color: #0369A1;
        }

        .badge-full {
          background: rgba(201, 182, 228, 0.9);
          color: #7C3AED;
        }

        .book-info {
          padding: 1.5rem;
        }

        .book-prompt {
          font-size: 1rem;
          font-weight: 600;
          color: var(--color-text-primary);
          margin-bottom: 1rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          line-height: 1.5;
        }

        .book-meta {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          margin-bottom: 1.25rem;
          font-size: 0.875rem;
          color: var(--color-text-secondary);
        }

        .book-meta-item {
          display: flex;
          align-items: center;
          gap: 0.375rem;
        }

        .download-btn-gallery {
          width: 100%;
          padding: 0.875rem 1.5rem;
          background: linear-gradient(135deg, #A8D8E6 0%, #C9B6E4 100%);
          color: white;
          border: none;
          border-radius: 14px;
          font-weight: 700;
          font-size: 0.9375rem;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          box-shadow: 0 4px 12px rgba(168, 216, 230, 0.3);
        }

        .download-btn-gallery:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(168, 216, 230, 0.5);
        }

        .download-btn-gallery:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .book-card {
          animation: fadeInUp 0.5s ease-out;
        }

        @media (max-width: 768px) {
          .gallery-title {
            font-size: 2rem;
          }

          .gallery-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
        }
      `}</style>

      <div className="gallery-container">
        <div className="gallery-header">
          <h2 className="gallery-title">
            <BookOpen size={36} style={{ display: 'inline-block', marginRight: '12px', verticalAlign: 'middle', color: '#B4A0E5' }} />
            <span className="gradient-text">{title}</span>
          </h2>
          <p className="gallery-subtitle">
            All your wonderful stories in one place
          </p>
        </div>

        <div className="gallery-grid">
          {books.map((book, index) => {
            const previewImages = book.previewUrls || [];
            const firstImage = previewImages[0];

            return (
              <div
                key={book.id}
                className="book-card"
                style={{
                  animationDelay: `${index * 0.05}s`
                }}
              >
                <div className="book-preview">
                  {firstImage ? (
                    <img
                      src={firstImage}
                      alt={book.prompt}
                      className="book-preview-img"
                    />
                  ) : (
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <BookOpen size={64} style={{ color: 'white', opacity: 0.5 }} />
                    </div>
                  )}
                  
                  <div className={`book-badge ${book.watermark ? 'badge-demo' : 'badge-full'}`}>
                    {book.watermark ? '🎨 Demo' : '✨ Full Book'}
                  </div>
                </div>

                <div className="book-info">
                  <div className="book-prompt">{book.prompt}</div>
                  
                  <div className="book-meta">
                    <div className="book-meta-item">
                      <BookOpen size={14} />
                      <span>{book.pages} pages</span>
                    </div>
                    <div className="book-meta-item">
                      <Calendar size={14} />
                      <span>{new Date(book.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {book.pdfUrl ? (
                    <a
                      href={book.pdfUrl}
                      download
                      className="download-btn-gallery"
                    >
                      <Download size={18} />
                      <span>Download Book</span>
                    </a>
                  ) : (
                    <button
                      className="download-btn-gallery"
                      disabled
                    >
                      <Loader2 size={18} className="loading-spinner" />
                      <span>Processing...</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

